import AsyncStorage from '@react-native-community/async-storage';
import firebase from 'firebase';
import moment, { unitOfTime, MomentInput } from 'moment';

import { previousPeriodEndDate } from './schedule';
import { CacheReport } from '../store/reducers/reportcache/reportcache.actionThunk';
import { ReportSections } from '../store/reducers/reportcache/reportcache.types';
import { Report, OrganizationSchedule, ReportSection, User } from './classes';

async function getUserProfile(): Promise<User & { uid: string }> {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('No authenticated user');
  return { uid: user.uid } as unknown as User & { uid: string };
}

function getCacheKeys(siteKey: string, reportKey: string) {
  return {
    reportCacheKey: `report/${siteKey}/${reportKey}`,
    summaryCacheKey: `reportIndex/${siteKey}/${reportKey}`
  };
}

function getDiffWithNow(time: MomentInput, unit: unitOfTime.Diff): number {
  const diff = moment().diff(time, unit);
  return Math.abs(diff);
}

function isExpiredReport(report: any, organizationSchedule: OrganizationSchedule) {
  const prevEnd = previousPeriodEndDate(organizationSchedule);

  if (report && report.datetimeIn) {
    const checkedIn = moment(report.datetimeIn);
    if (prevEnd && checkedIn.isSameOrBefore(prevEnd, 'day') && report.status === 'draft') {
      return true;
    }
  }
  return false;
}

function getSavedPhotos(reports: (Report | ReportSection)[]) {
  let savedPhotos: string[] = [];
  reports.forEach(report => {
    if (report.questions) {
      report.questions.forEach(({ photos }) => {
        if (photos) {
          savedPhotos = savedPhotos.concat(photos);
        }
      });
    }
  });
  return savedPhotos;
}

async function cleanUpExpiredReports(expiredReports: CacheReport[]) {
  const removeItems = expiredReports
    .filter(({ type }) => type !== 'unavailable')
    .map(({ type, siteKey, reportKey, sectionIdx }) => {
      const itemToRemove =
        type !== 'reportSection'
          ? `${type}/${siteKey}/${reportKey}`
          : `reportSection/${siteKey}/${reportKey}/${sectionIdx}`;
      return AsyncStorage.removeItem(itemToRemove);
    });

  try {
    await Promise.all(removeItems);
    return Promise.resolve();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
    throw error;
  }
}

function mergeSiteReportIndex(siteReportIndex?: any, siteCacheReportIndex?: any) {
  let mergedSiteReportIndex = siteReportIndex ? { ...siteReportIndex } : null;
  if (siteCacheReportIndex) {
    mergedSiteReportIndex = { ...mergedSiteReportIndex, ...siteCacheReportIndex };
  }
  return mergedSiteReportIndex;
}

const cleanReportSectionPhotos = (reportSections: ReportSections, sectionIdx?: number) => {
  const allPhotos: Promise<any>[] = [];
  for (const index of Object.keys(reportSections)) {
    if (sectionIdx !== undefined && !isNaN(sectionIdx) && Number(index) !== sectionIdx) {
      continue;
    }
    if (reportSections[Number(index)]) {
      const { questions } = reportSections[Number(index)];
      questions.forEach(({ photos }) => {
        if (photos && photos.length) {
          allPhotos.push(...makePhotoPromise(photos));
        }
      });
    }
  }
  if (allPhotos.length) {
    return Promise.all(allPhotos);
  }
  return Promise.resolve();
};

const makePhotoPromise = (uris: string[]) => {
  const promises = uris.map(async uri => {
    return Promise.resolve();
  });
  return promises;
};

export {
  getUserProfile,
  getCacheKeys,
  getDiffWithNow,
  isExpiredReport,
  cleanUpExpiredReports,
  mergeSiteReportIndex,
  getSavedPhotos,
  cleanReportSectionPhotos,
  makePhotoPromise
};
